/**
 * The app's top-level routing decision, as a pure function.
 *
 * ADR 0006 makes "what the user must finish before the app opens" a routing
 * decision instead of overlay z-order. Keeping the branching here — with no
 * React and no expo-router — is what lets it be covered exhaustively without
 * mocking the router. Layouts are thin wiring around this result.
 */

/** Which of the four mutually exclusive tiers the root layout should mount. */
export type GateOutcome = 'booting' | 'auth' | 'setup' | 'app';

export type GateInput = {
  /** Meaning is unchanged from `useAuthStatus()`; ADR 0006 deliberately did not extend it. */
  authStatus: 'loading' | 'loggedOut' | 'unverified' | 'verified';
  /** True once auth has produced a non-loading status at least one time. */
  hasResolvedOnce: boolean;
  /** The outcome last returned, used to hold steady through a loading blip. */
  previousOutcome: GateOutcome | null;
  /** Auth-session claim; applies only to social signups. */
  usernamePending: boolean;
  /** Gateway guided-experience `onboarding` is still eligible. */
  onboardingRequired: boolean;
  /** The eligibility query has data, fresh or from cache. */
  eligibilityResolved: boolean;
  /** The eligibility query errored with no usable data. */
  eligibilityError: boolean;
};

export function resolveGate(input: GateInput): GateOutcome {
  const {
    authStatus,
    hasResolvedOnce,
    previousOutcome,
    usernamePending,
    onboardingRequired,
    eligibilityResolved,
    eligibilityError,
  } = input;

  if (authStatus === 'loading') {
    // Cold boot: nothing to hold, so show the splash.
    if (!hasResolvedOnce) return 'booting';
    // After the first resolve, a `loading` blip (tab refocus, session revalidate)
    // must not change the mounted tier — otherwise the Stack remounts and a user
    // mid-setup is bounced out on every refocus.
    return previousOutcome ?? 'booting';
  }

  if (authStatus === 'loggedOut' || authStatus === 'unverified') return 'auth';

  // Verified from here down.

  // The username claim rides the auth session and is synchronous, so it never
  // waits on (or is overridden by) the eligibility query.
  if (usernamePending) return 'setup';

  // Fail open. A brand-new user is online by definition, so an eligibility
  // failure lands almost entirely on returning users, who must not be dumped
  // into a flow they already finished. Failing closed is a hard trap: the setup
  // PATCH calls would fail too.
  if (eligibilityError) return 'app';

  if (!eligibilityResolved) return 'booting';

  return onboardingRequired ? 'setup' : 'app';
}
